import React from 'react';
import { useReservations } from '../ReservationContext';

const ZonesView = ({ onOpenCreate, onOpenEdit }) => {
  const {
    zones,
    unassignedTables,
    zoneAvailability,
    zoneAvailabilityDate,
    setZoneAvailabilityDate,
    fetchZoneAvailability,
    assignTableToZone,
    removeTableFromZone,
    deleteZone,
    setError,
    loading,
  } = useReservations();

  const handleDeleteZone = async (zone) => {
    if (zone.is_protected) {
      setError('Cannot delete protected default zone');
      return;
    }
    if (zone.table_count > 0) {
      setError(`Cannot delete zone with ${zone.table_count} tables assigned. Reassign tables first.`);
      return;
    }
    if (!window.confirm(`Are you sure you want to delete zone "${zone.zone_name}"?`)) return;
    await deleteZone(zone);
  };

  const handleRemoveTable = async (zoneId, tableId) => {
    if (!window.confirm(`Remove table ${tableId} from this zone?`)) return;
    await removeTableFromZone(zoneId, tableId);
  };

  return (
    <div className="zones-view">
      <div className="zones-toolbar">
        <div className="toolbar-left">
          <h3>Seating Zones</h3>
        </div>
        <div className="toolbar-right">
          <div className="date-picker-group">
            <label>Check Availability:</label>
            <input
              type="date"
              value={zoneAvailabilityDate}
              onChange={(e) => {
                setZoneAvailabilityDate(e.target.value);
                fetchZoneAvailability(e.target.value);
              }}
            />
          </div>
          <button className="btn btn-primary" onClick={onOpenCreate}>
            + Add Zone
          </button>
        </div>
      </div>

      {/* Zone Availability Notice */}
      {zoneAvailability && zoneAvailability.blocked_zones && zoneAvailability.blocked_zones.length > 0 && (
        <div className="alert alert-warning">
          <strong>Private Events on {zoneAvailabilityDate}:</strong>
          {zoneAvailability.blocked_zones.map((blocked, index) => (
            <div key={`${blocked.zone_id}-${index}`} className="blocked-zone-info">
              <span className="blocked-zone-name">{blocked.zone_id}</span>
              {blocked.blocked_by_zone ? (
                <span className="blocked-reason"> - Blocked by {blocked.blocked_by_zone} booking</span>
              ) : (
                <span className="blocked-event"> - {blocked.event_title}</span>
              )}
              {blocked.start_time && blocked.end_time && (
                <span className="blocked-time"> ({blocked.start_time} - {blocked.end_time})</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Zones Grid */}
      <div className="zones-grid">
        {zones.map((zone) => (
          <div
            key={zone.zone_id}
            className={`zone-card ${zone.is_active ? '' : 'zone-inactive'} ${zone.is_private_room ? 'zone-private' : ''}`}
          >
            <div className="zone-header">
              <div className="zone-title">
                <span className="zone-name">{zone.zone_name}</span>
                <span className="zone-floor">Floor {zone.floor}</span>
              </div>
              <div className="zone-badges">
                {zone.is_protected && (
                  <span className="zone-badge default-badge">Default</span>
                )}
                {zone.is_private_room && (
                  <span className="zone-badge private-badge">Private Room</span>
                )}
              </div>
            </div>
            <div className="zone-body">
              {zone.description && (
                <div className="zone-description">{zone.description}</div>
              )}
              <div className="zone-stats">
                <span className="zone-stat">
                  <strong>{zone.table_count}</strong> tables
                </span>
                <span className="zone-stat">
                  <strong>{zone.total_capacity}</strong> seats
                </span>
                <span className="zone-stat">
                  Party: {zone.min_party_size}-{zone.max_party_size}
                </span>
              </div>

              {/* Tables in Zone */}
              <div className="zone-tables">
                <div className="zone-tables-header">
                  <span>Tables</span>
                  {unassignedTables.length > 0 && (
                    <select
                      className="assign-table-select"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          assignTableToZone(zone.zone_id, e.target.value);
                        }
                      }}
                    >
                      <option value="">+ Add Table</option>
                      {unassignedTables.map((t) => (
                        <option key={t.table_id} value={t.table_id}>
                          {t.table_id} ({t.capacity} seats)
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="zone-table-list">
                  {zone.tables && zone.tables.length > 0 ? (
                    zone.tables.map((table) => (
                      <div key={table.table_id} className="zone-table-item">
                        <span className="zone-table-id">{table.table_id}</span>
                        <span className="zone-table-capacity">{table.capacity} seats</span>
                        <button
                          className="zone-table-remove"
                          onClick={() => handleRemoveTable(zone.zone_id, table.table_id)}
                          title="Remove from zone"
                        >
                          &times;
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="zone-no-tables">No tables assigned</div>
                  )}
                </div>
              </div>
            </div>
            <div className="zone-actions">
              <button
                className="action-btn edit-btn"
                onClick={() => onOpenEdit(zone)}
              >
                Edit
              </button>
              {!zone.is_protected && (
                <button
                  className="action-btn delete-btn"
                  onClick={() => handleDeleteZone(zone)}
                  disabled={loading}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Unassigned Tables Section */}
      {unassignedTables.length > 0 && (
        <div className="unassigned-tables-section">
          <h4>Unassigned Tables ({unassignedTables.length})</h4>
          <div className="unassigned-tables-list">
            {unassignedTables.map((table) => (
              <div key={table.table_id} className="unassigned-table-item">
                <span className="table-id">{table.table_id}</span>
                <span className="table-info">{table.table_type} - {table.capacity} seats</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ZonesView;
